import { useState, ReactNode, ReactElement, cloneElement, useEffect } from "react";
import { Button, Listbox, ListboxItem, ListboxProps } from "@heroui/react";
import { ArrowLeftIcon, IconProps } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import clsx from "clsx";
import { POPOVER_MIN_WIDTH_CLASS } from "@/features/notes/config";

export interface NestedAction<T = string> {
  key: T;
  label: string;
  icon?: ReactElement<IconProps>;
  content?: ReactNode;
  nestedActions?: () => NestedAction<T>[];
  run?: () => void;
}

export interface ContentItem {
  content: ReactNode;
  title?: string;
}

export interface NestedContentNavigatorProps<T = string> {
  /** Array of root actions to display */
  actions: NestedAction<T>[];
  /** Callback when an action's run method is called */
  onActionRun?: (action: NestedAction<T>) => void;
  /** Custom renderer for action items */
  renderAction?: (action: NestedAction<T>, onPress: () => void) => ReactNode;
  /** Additional CSS classes for the container */
  className?: string;
  /** Additional HTML attributes for the content container */
  classNames?: {
    content?: string;
    header?: string;
  };
  /** Initial content stack to start with (for pre-navigation) */
  initialStack?: ContentItem[];
  selectionMode?: ListboxProps["selectionMode"];
  selectedKeys?: ListboxProps["selectedKeys"];
}

export function NestedContentNavigator<T = string>({
  actions,
  className,
  classNames,
  initialStack = [],
  renderAction,
  selectedKeys,
  selectionMode,
  onActionRun,
}: NestedContentNavigatorProps<T>) {
  const [contentStack, setContentStack] = useState<ContentItem[]>(initialStack);
  const [isNavigatingBack, setIsNavigatingBack] = useState(false);

  const currentContent = contentStack[contentStack.length - 1];
  const isInNestedContent = contentStack.length > 0;

  // Reset navigation direction after animation completes
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsNavigatingBack(false);
    }, 350); // Slightly longer than the longest animation duration

    return () => clearTimeout(timer);
  }, [contentStack.length]);

  function pushContent(content: ReactNode, title?: string) {
    setIsNavigatingBack(false); // Going forward/deeper
    setContentStack((prev) => {
      const newStack = [...prev, { content, title }];

      return newStack;
    });
  }

  function popContent() {
    setIsNavigatingBack(true); // Going back/up
    setContentStack((prev) => {
      const newStack = prev.slice(0, -1);

      return newStack;
    });
  }

  function handleActionPress(action: NestedAction<T>) {
    if (action.content) {
      pushContent(action.content, action.label);
    } else if (action.nestedActions) {
      const nestedContent = renderActionsList(action.nestedActions());
      pushContent(nestedContent, action.label);
    } else if (action.run) {
      action.run();
      onActionRun?.(action);
    }
  }

  function renderActionsList(actionsList: NestedAction<T>[]) {
    if (renderAction) {
      return (
        <div className="flex flex-col">
          {actionsList.map((action) => (
            <div key={String(action.key)}>{renderAction(action, () => handleActionPress(action))}</div>
          ))}
        </div>
      );
    }

    return (
      <Listbox aria-label="Actions" items={actionsList} selectionMode={selectionMode} selectedKeys={selectedKeys}>
        {(action) => (
          <ListboxItem
            className="py-2"
            classNames={{ title: "text-md" }}
            key={String(action.key)}
            startContent={action.icon ? cloneElement(action.icon, { ...action.icon.props, size: 18 }) : null}
            onPress={() => handleActionPress(action)}
          >
            {action.label}
          </ListboxItem>
        )}
      </Listbox>
    );
  }

  function renderHeader(onBack: () => void, title?: string) {
    return (
      <div className={clsx("flex items-center gap-2 px-1 pt-1 pb-2", classNames?.header)}>
        <Button isIconOnly size="sm" variant="light" onPress={onBack} aria-label="Go back">
          <ArrowLeftIcon size={20} weight="bold" />
        </Button>
        {title && <span className="text-md line-clamp-1 font-semibold">{title}</span>}
      </div>
    );
  }

  return (
    <div className={clsx("flex h-full flex-col overflow-hidden", className, POPOVER_MIN_WIDTH_CLASS)}>
      <AnimatePresence mode="wait">
        {isInNestedContent ? (
          <motion.div
            key={`nested-level-${contentStack.length}`} // Use stack length as key for unique animations
            initial={{
              x: isNavigatingBack ? -25 : 25,
              opacity: 0,
            }}
            animate={{ x: 0, opacity: 1 }}
            exit={{
              x: isNavigatingBack ? 25 : -25,
              opacity: 0,
            }}
            transition={{
              duration: 0.3,
              ease: "easeInOut",
            }}
            className="flex flex-col"
          >
            {renderHeader(popContent, currentContent?.title)}
            <div className={clsx("flex-1 overflow-x-hidden overflow-y-auto p-1", classNames?.content)}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={`content-${contentStack.length}`}
                  initial={{
                    x: isNavigatingBack ? -15 : 15,
                    opacity: 0,
                  }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{
                    x: isNavigatingBack ? 15 : -15,
                    opacity: 0,
                  }}
                  transition={{
                    duration: 0.25,
                    ease: "easeInOut",
                  }}
                >
                  {currentContent?.content}
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="root-actions"
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 20, opacity: 0 }}
            transition={{
              duration: 0.3,
              ease: "easeInOut",
            }}
            className="overflow-x-hidden"
          >
            {renderActionsList(actions)}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
